/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import React, { ButtonHTMLAttributes, cloneElement, isValidElement, ReactElement } from 'react'

import { cn } from '@/utils/css'

/** Variants for the button component. */
const buttonVariants = cva(
  'flex items-center justify-center max-w-full rounded-sm font-medium cursor-pointer transition-colors duration-200 ease-in-out motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        primary: 'bg-primary-500 text-white hover:bg-primary-600',
        secondary: 'bg-secondary-500 text-graphite-900 hover:bg-secondary-600',
        grey: 'bg-gray-700 text-white hover:bg-gray-800',
        outlined: 'bg-transparent ring-2 ring-inset hover:bg-opacity-10',
        'outlined-white': 'bg-transparent ring-2 ring-white text-white hover:bg-opacity-10',
        ghost: 'bg-transparent hover:bg-opacity-10',
        'white-primary': 'bg-white text-primary-500 hover:bg-primary-50',
        danger: 'bg-danger-500 text-white hover:bg-danger-800',
      },
      size: {
        sm: 'px-2 py-1 text-xs min-h-8',
        md: 'px-3 py-2 text-sm min-h-10',
        lg: 'px-4 py-2 text-base min-h-12',
        xl: 'px-6 py-3 text-lg min-h-14',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
      uppercase: {
        true: 'uppercase',
        false: 'normal-case',
      },
      rounded: {
        none: 'rounded-none',
        sm: 'rounded-xs',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        '2xl': 'rounded-2xl',
        '3xl': 'rounded-3xl',
        full: 'rounded-full',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
        false: '',
      },
      hasIcon: {
        true: 'items-center',
        false: '',
      },
      iconPosition: {
        left: 'flex-row',
        right: 'flex-row-reverse',
      },
    },
    compoundVariants: [
      {
        variant: 'outlined',
        class: 'ring-current',
      },
      {
        variant: ['outlined', 'ghost'],
        class: 'text-current',
      },
      {
        hasIcon: true,
        size: 'sm',
        class: 'gap-1',
      },
      {
        hasIcon: true,
        size: ['md', 'lg', 'xl'],
        class: 'gap-2',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
      uppercase: false,
      rounded: 'md',
      disabled: false,
      hasIcon: false,
      iconPosition: 'left',
    },
  },
)

/** Get the size of the icon based on the size of the button. */
const getIconSize = (size: VariantProps<typeof buttonVariants>['size'], isIconOnly: boolean): number => {
  if (isIconOnly) {
    switch (size) {
      case 'sm':
        return 16
      case 'md':
        return 20
      case 'lg':
        return 24
      case 'xl':
        return 28
      default:
        return 20
    }
  }

  switch (size) {
    case 'sm':
      return 14
    case 'md':
      return 18
    case 'lg':
      return 20
    case 'xl':
      return 24
    default:
      return 18
  }
}

/** The props the button sets on a supplied icon when it scales it to match the button size. */
type ScalableIconProps = {
  'aria-hidden'?: boolean
  size?: number
  style?: React.CSSProperties
  width?: number
  height?: number
}

/** A description of available properties for this component. */
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    /** Merge button behaviour and styles into a single child element such as a link. */
    asChild?: boolean
    icon?: React.ReactNode
    loading?: boolean
  }

/**
 * A highly customisable button component with a set of predefined variants and properties.
 */
const Button: React.FC<ButtonProps> = ({
  variant,
  size,
  fullWidth,
  uppercase,
  rounded,
  disabled,
  className,
  children,
  icon,
  loading,
  asChild = false,
  iconPosition = 'left',
  ...props
}) => {
  const hasIcon = !!icon || loading
  const isIconOnly = !children
  const iconSize = getIconSize(size, isIconOnly)

  const renderIcon = () => {
    if (loading) {
      return <Loader2 aria-hidden={'true'} className={'animate-spin motion-reduce:animate-none'} size={iconSize} />
    }

    if (!icon) return null

    if (isValidElement(icon)) {
      const iconElement = icon as ReactElement<ScalableIconProps>
      if ('size' in iconElement.props) {
        return cloneElement(iconElement, { 'aria-hidden': true, size: iconSize })
      }

      return cloneElement(iconElement, {
        'aria-hidden': true,
        style: { width: `${iconSize}px`, height: `${iconSize}px` },
        width: iconSize,
        height: iconSize,
      })
    }

    return icon
  }

  const Component = asChild ? Slot : 'button'
  const isDisabled = disabled || loading
  const { onClick, type, ...elementProps } = props

  return (
    <Component
      className={cn(
        buttonVariants({
          variant,
          size,
          fullWidth,
          uppercase,
          rounded,
          disabled,
          hasIcon,
          iconPosition,
          className,
        }),
        asChild && isDisabled && 'pointer-events-none',
        !children && [
          'aspect-square',
          size === 'sm' && 'w-8',
          size === 'md' && 'w-10',
          size === 'lg' && 'w-12',
          size === 'xl' && 'w-14',
          'inline-flex items-center justify-center',
        ],
      )}
      {...(asChild
        ? { 'aria-disabled': isDisabled || undefined, tabIndex: isDisabled ? -1 : undefined }
        : { 'aria-busy': loading || undefined, disabled: isDisabled, type: type ?? 'button' })}
      {...(onClick
        ? {
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
              if (asChild && isDisabled) {
                event.preventDefault()
                return
              }
              onClick(event)
            },
          }
        : {})}
      {...elementProps}
    >
      {renderIcon()}
      <Slottable>{children}</Slottable>
    </Component>
  )
}

export { Button, buttonVariants, type ButtonProps }
